import { i18n } from '@apps/main/modules/localization/localizationSetup';
import type { RecipeIngredient } from '@apps/main/modules/recipe/ingredients';
import type { OwnStayIngredient } from '@apps/main/modules/recipe/ingredients/ownStayIngredient';

import { isOwnStayIngredientResponseWithStaticInfo } from '@libs/search-service-smart-search';
import type {
  ActivityStaticInfo,
  ActivitySuggestionWithStaticInfo,
  ChronologicalItinerary,
  ItineraryMainIngredientResponseWithStaticInfo,
} from '@libs/trip-planner-interfaces';

import type { ItinerarySegment } from '../../models';
import { ItinerarySegmentType } from '../../models';
import type { ItineraryOwnStaySegment } from '../../models/itinerarySegments/ownStay';

import {
  getActivitySegments,
  getUppsellSegment,
} from './tripOverviewActivitySegmentsLogic';
import {
  getCityText,
  getTripOverviewDateLocationSegment,
} from './tripOverviewLocationSegmentsLogic';

const { t } = i18n.global;

interface GetOwnStayRelatedSegmentsParams {
  ingredient: ItineraryMainIngredientResponseWithStaticInfo;
  getIngredient: (id: string) => RecipeIngredient;
  ingredientIndex: number;
  areThereActivitiesInDestination: boolean;
  activitySuggestions: ActivitySuggestionWithStaticInfo[];
  staticActivitySuggestions: ActivityStaticInfo[];
  moneyFormanterFn: (amount: number, currency: string) => string;
  itineraryByPeriod: ChronologicalItinerary;
  selectedDate?: Date;
}

export function getOwnStayRelatedSegments(
  params: GetOwnStayRelatedSegmentsParams
): ItinerarySegment[] {
  const segments: ItinerarySegment[] = [];
  if (isOwnStayIngredientResponseWithStaticInfo(params.ingredient)) {
    const ownStayIngredient = params.getIngredient(
      params.ingredient.ingredientId
    ) as OwnStayIngredient;

    segments.push(
      getTripOverviewDateLocationSegment(params.itineraryByPeriod, {
        ingredientId: params.ingredient.ingredientId,
        numberOfNights: params.ingredient.numberOfNights,
        displayText: ownStayIngredient.displayText,
      })
    );

    if (params.ingredient.numberOfNights !== 0) {
      const ownStaySegment: ItineraryOwnStaySegment = {
        segmentType: ItinerarySegmentType.OwnStay,
        addAccommodationMessage: `${t(
          'planner.no_availability.actions.explore_hotels_selection_in'
        )} ${getCityText(ownStayIngredient.displayText)}`,
        context: {
          ingredientId: params.ingredient.ingredientId,
          ingredientIndex: params.ingredientIndex,
        },
      };

      segments.push(ownStaySegment);
    }

    segments.push(
      ...getActivitySegments(
        params.ingredient,
        params.getIngredient,
        params.ingredientIndex,
        params.selectedDate
      )
    );

    const upsellSegment = getUppsellSegment(
      ownStayIngredient,
      params.areThereActivitiesInDestination,
      params.activitySuggestions,
      params.staticActivitySuggestions,
      params.moneyFormanterFn
    );

    if (upsellSegment) {
      segments.push(upsellSegment);
    }
  }
  return segments;
}
